import React from "react";
import { Link, NavLink, useLocation, useNavigate, useParams } from "react-router-dom";
import { Accordion } from "react-bootstrap";
import { useReactiveVar } from "@apollo/client";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";

type AccordionItem = {
    label: string;
    to: string;
    href?: string;
    active: boolean;
};

type AccordionNavProps = {
    title: string;
    items: AccordionItem[];
    icon?: string;
    eventKey: string;
    handleCloseNavbar?: () => void;
    firstItemLink?: string;
    active: boolean;
    inactiveLink?: string;
    iconPath?: string | React.ComponentType<React.SVGProps<SVGSVGElement>>;
    canShowInactiveLink?: boolean;
};
const AccordionNav: React.FC<AccordionNavProps> = ({
    title,
    items,
    active,
    icon,
    eventKey,
    handleCloseNavbar,
    firstItemLink,
    inactiveLink,
    iconPath,
    canShowInactiveLink = true,
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const isRegistrationOrCreateOwner = location.pathname === "/registration" || location.pathname === "/create-owner";

    return (
        <Accordion.Item eventKey={eventKey} className="mb-1">
            <div className="position-relative">
                <Accordion.Header
                    className={`d-flex justify-between align-items-center ${!active && !isRegistrationOrCreateOwner ? "opacity-25 pe-none" : ""}`}
                >
                    <div
                        className={`d-flex align-items-center w-100 ${!active && !isRegistrationOrCreateOwner ? "pe-4" : ""}`}
                    >
                        <div className="btn btn-accordion-icon d-flex justify-center align-items-center p-1 me-2 z-5">
                            <i className="bi bi-caret-right-fill fs-10 w-100"></i>
                        </div>
                        <span
                            className="d-flex align-items-center text-dark w-100"
                            role="link"
                            onClick={() => {
                                if (active && firstItemLink && !isRegistrationOrCreateOwner) {
                                    navigate(`/${firstItemLink}`);
                                    handleCloseNavbar?.();
                                }
                            }}
                        >
                            {iconPath ? (
                                <div className="me-2">{React.createElement(iconPath)}</div>
                            ) : (
                                <i className={`bi ${icon} me-2`}></i>
                            )}

                            <span className="w-100">{title}</span>
                        </span>
                    </div>
                </Accordion.Header>
                {!active && !isRegistrationOrCreateOwner && canShowInactiveLink ? (
                    <Link
                        to={
                            inactiveLink
                                ? inactiveLink
                                : `payment/company/${currentCompanyId}/modules/branch/${branchId}`
                        }
                        className="position-absolute end-0 top-50 translate-middle-y text-primary text-decoration-none me-3 me-xl-1 me-xxl-3"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleCloseNavbar?.();
                        }}
                    >
                        <i className="bi bi-plus-lg fs-8"></i>
                    </Link>
                ) : null}
            </div>

            <Accordion.Body className="ms-4 px-2 py-0 mt-1">
                {items.map((item, index) => {
                    const { to, href, label, active } = item;

                    const cleanPath = to?.split("/").slice(0, -1).join("/") || "";
                    const pathname = location.pathname;

                    const isEmployeeEditSelected = to === "employees-list" && pathname.includes("edit");
                    const isMeBookingMatch = to?.startsWith("me-booking/") && pathname.startsWith(`/${to}`);
                    const isAccordionActive =
                        !to?.startsWith("me-booking/") &&
                        (`/${to}` === cleanPath ||
                            (pathname.startsWith(`/${cleanPath}`) && !pathname.includes("add-employee")));
                    const isServiceProviderMatch =
                        pathname.startsWith("/services-providers") && to?.includes("service-list");
                    const isLogbookMatch =
                        pathname.startsWith("/products/write-off") && to?.includes("logbook/journal");
                    const isWarehouse =
                        pathname.startsWith("/warehouses") &&
                        to?.includes("warehouses/branch") &&
                        !pathname.includes("/operations") &&
                        !pathname.includes("/sales-statistics");
                    const isWarehouseProduct =
                        pathname.startsWith("/warehouse/product/") &&
                        to?.includes(`warehouse/product/${currentCompanyId}/`);
                    const isWarehouseTechnologicalMaps =
                        pathname.startsWith("/warehouse/technological-maps") &&
                        to?.includes("warehouse/technological-maps");
                    const isModules =
                        pathname.startsWith("/payment/company") &&
                        to?.includes("/modules/") &&
                        pathname.includes("/modules");
                    const isLogbookRecord =
                        to?.includes("logbook/journal") &&
                        (pathname.includes("logbook/edit-record/") || pathname.includes("change-history"));
                    const isMenu =
                        to?.includes("all-menus") &&
                        (pathname.startsWith("/menu/page") ||
                            pathname.includes("preview") ||
                            pathname.includes("before-publish"));
                    const isDishesMenu =
                        to?.includes("dishes/branch") &&
                        (pathname.includes("create-dish") || pathname.includes("update-dish"));

                    const isItemHighlighted =
                        isAccordionActive ||
                        isMeBookingMatch ||
                        isEmployeeEditSelected ||
                        isServiceProviderMatch ||
                        isLogbookMatch ||
                        isWarehouse ||
                        isWarehouseProduct ||
                        isWarehouseTechnologicalMaps ||
                        isModules ||
                        isLogbookRecord ||
                        isMenu ||
                        isDishesMenu;

                    return (
                        <div
                            key={index}
                            className={`accordion-body-item mb-1 rounded ${!active ? "opacity-25 pe-none" : ""}`}
                        >
                            {to ? (
                                <NavLink
                                    to={to}
                                    end
                                    className={({
                                        isActive,
                                    }) => `accordion-body-item-link text-decoration-none text-dark d-block py-2 px-4 h-100 w-100 rounded-1 
                                         ${isActive || isItemHighlighted ? "bg-additional-violet" : ""}`}
                                    onClick={handleCloseNavbar}
                                >
                                    {label}
                                </NavLink>
                            ) : (
                                <a
                                    href={href}
                                    className="accordion-body-item-link text-decoration-none text-dark d-block py-2 ps-4 h-100 w-100"
                                >
                                    {label}
                                </a>
                            )}
                        </div>
                    );
                })}
            </Accordion.Body>
        </Accordion.Item>
    );
};

export default AccordionNav;
